'use client';

import {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';

import classNames from 'classnames';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-client';
import { createPortal } from 'react-dom';
import { useTimeoutFn } from 'react-use';

import CloseIcon from '/public/icons/close.svg';
import ErrorIcon from '/public/icons/error.svg';
import InfoIcon from '/public/icons/info.svg';
import SuccessIcon from '/public/icons/success.svg';

import styles from './style.module.css';

interface Notification {
  id: number;
  subMessage?: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface NotificationContextType {
  notifications: Notification[];
  pushNotification: ({
    subMessage,
    message,
    type,
  }: {
    subMessage?: string;
    message: string;
    type: 'success' | 'error' | 'info';
  }) => void;
  removeNotification: (id: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

const NOTIFICATION_TIMEOUT_MS = 3000;
const ERROR_NOTIFICATION_TIMEOUT_MS = 5000;

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }
  return context;
};

const NotificationItem = ({
  notification,
  removeNotification,
  fromEnd,
  expanded,
}: {
  notification: Notification;
  removeNotification: (id: number) => void;
  fromEnd: number;
  expanded: boolean;
}) => {
  const timeout =
    notification.type === 'error'
      ? ERROR_NOTIFICATION_TIMEOUT_MS
      : NOTIFICATION_TIMEOUT_MS;

  useTimeoutFn(() => removeNotification(notification.id), timeout);

  return (
    <div
      className={styles.itemWrapper}
      style={
        !expanded
          ? {
              transform: `translateY(${fromEnd * -8}px) scale(${1 - fromEnd * 0.04})`,
              opacity: fromEnd > 2 ? 0 : 1,
              zIndex: 100 - fromEnd,
            }
          : { zIndex: 100 - fromEnd }
      }
    >
      <motion.div
        className={classNames(styles.notification, {
          [styles._success || '']: notification.type === 'success',
          [styles._error || '']: notification.type === 'error',
          [styles._info || '']: notification.type === 'info',
        })}
        initial={{ opacity: 0, x: '100%' }}
        animate={{
          opacity: 1,
          x: 0,
          transition: { duration: 0.35, ease: [0.33, 0, 0, 1] },
        }}
        exit={{
          opacity: 0,
          x: '100%',
          transition: { duration: 0.2 },
        }}
      >
        {notification.type === 'success' && (
          <SuccessIcon width={24} height={24} />
        )}
        {notification.type === 'error' && <ErrorIcon width={24} height={24} />}
        {notification.type === 'info' && <InfoIcon width={24} height={24} />}
        <div className={styles.content}>
          <h1 className={styles.message}>{notification.message}</h1>
          {notification.subMessage && (
            <p className={styles.subMessage}>{notification.subMessage}</p>
          )}
        </div>
        <button
          className={styles.closeButton}
          onClick={() => removeNotification(notification.id)}
        >
          <CloseIcon width={16} height={16} />
        </button>
      </motion.div>
    </div>
  );
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const nextIdRef = useRef(0);

  useEffect(() => {
    setInitialized(true);
  }, []);

  const pushNotification = useCallback(
    ({
      subMessage,
      message,
      type,
    }: {
      subMessage?: string;
      message: string;
      type: 'success' | 'error' | 'info';
    }) => {
      const id = nextIdRef.current;
      nextIdRef.current += 1;
      setNotifications((prev) => [...prev, { id, message, type, subMessage }]);
      setExpanded(false);
    },
    [],
  );

  const removeNotification = useCallback(
    (id: number) => {
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== id),
      );
    },
    [setNotifications],
  );

  return (
    <NotificationContext.Provider
      value={{ notifications, pushNotification, removeNotification }}
    >
      {children}
      {initialized &&
        notifications.length > 0 &&
        createPortal(
          <div
            className={classNames(
              styles.notifications,
              expanded && styles.notificationsExpanded,
            )}
            onMouseEnter={() => {
              if (notifications.length > 1) setExpanded(true);
            }}
            onMouseLeave={() => setExpanded(false)}
          >
            <AnimatePresence>
              {notifications.map((notification, i) => {
                const fromEnd = notifications.length - 1 - i;
                return (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    removeNotification={removeNotification}
                    fromEnd={fromEnd}
                    expanded={expanded}
                  />
                );
              })}
            </AnimatePresence>
          </div>,
          document.body,
        )}
    </NotificationContext.Provider>
  );
};
